import { useEffect, useState } from 'react';
import { X, CheckCircle, XCircle, AlertCircle, Info } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { Notification } from '@/store/types';

interface NotificationItemProps {
  notification: Notification;
  onDismiss: (id: string) => void;
}

export function NotificationItem({ notification, onDismiss }: NotificationItemProps) {
  const [isExiting, setIsExiting] = useState(false);
  const [progress, setProgress] = useState(100);

  // Handle auto-dismiss with progress bar
  useEffect(() => {
    if (!notification.duration || notification.duration === 0) return;

    const interval = 50; // Update every 50ms for smooth animation
    const decrementAmount = (100 * interval) / notification.duration;

    const timer = setInterval(() => {
      setProgress((prev) => {
        const newProgress = prev - decrementAmount;
        if (newProgress <= 0) {
          handleDismiss();
          return 0;
        }
        return newProgress;
      });
    }, interval);

    return () => clearInterval(timer);
  }, [notification.duration]);

  const handleDismiss = () => {
    setIsExiting(true);
    setTimeout(() => {
      onDismiss(notification.id);
    }, 300); // Match animation duration
  };

  // Icon and color based on notification type
  const typeConfig = {
    success: {
      icon: CheckCircle,
      bgColor: 'bg-green-50 dark:bg-green-900/20',
      borderColor: 'border-green-200 dark:border-green-800',
      iconColor: 'text-green-600 dark:text-green-400',
      progressColor: 'bg-green-600 dark:bg-green-400',
    },
    error: {
      icon: XCircle,
      bgColor: 'bg-red-50 dark:bg-red-900/20',
      borderColor: 'border-red-200 dark:border-red-800',
      iconColor: 'text-red-600 dark:text-red-400',
      progressColor: 'bg-red-600 dark:bg-red-400',
    },
    warning: {
      icon: AlertCircle,
      bgColor: 'bg-amber-50 dark:bg-amber-900/20',
      borderColor: 'border-amber-200 dark:border-amber-800',
      iconColor: 'text-amber-600 dark:text-amber-400',
      progressColor: 'bg-amber-600 dark:bg-amber-400',
    },
    info: {
      icon: Info,
      bgColor: 'bg-blue-50 dark:bg-blue-900/20',
      borderColor: 'border-blue-200 dark:border-blue-800',
      iconColor: 'text-blue-600 dark:text-blue-400',
      progressColor: 'bg-blue-600 dark:bg-blue-400',
    },
  };

  const config = typeConfig[notification.type];
  const Icon = config.icon;

  return (
    <div
      className={cn(
        'relative overflow-hidden rounded-lg border shadow-lg transition-all duration-300',
        'bg-white dark:bg-zinc-800',
        config.borderColor,
        isExiting ? 'translate-x-full opacity-0' : 'translate-x-0 opacity-100'
      )}
      role="alert"
      aria-live="polite"
    >
      <div className={cn('p-4', config.bgColor)}>
        <div className="flex items-start gap-3">
          {/* Icon */}
          <Icon className={cn('mt-0.5 h-5 w-5 flex-shrink-0', config.iconColor)} />

          {/* Content */}
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-zinc-900 dark:text-zinc-100">
              {notification.title}
            </p>
            {notification.message && (
              <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-400">
                {notification.message}
              </p>
            )}
            {notification.action && (
              <button
                onClick={notification.action.onClick}
                className="mt-2 text-sm font-medium text-blue-600 hover:text-blue-700 focus:underline focus:outline-none dark:text-blue-400 dark:hover:text-blue-300"
              >
                {notification.action.label}
              </button>
            )}
          </div>

          {/* Dismiss button */}
          <button
            onClick={handleDismiss}
            className="flex-shrink-0 rounded-md p-1 hover:bg-zinc-200 focus:ring-2 focus:ring-zinc-500 focus:outline-none dark:hover:bg-zinc-700 dark:focus:ring-zinc-400"
            aria-label="Dismiss notification"
          >
            <X className="h-4 w-4 text-zinc-500 dark:text-zinc-400" />
          </button>
        </div>
      </div>

      {/* Progress bar for auto-dismiss */}
      {notification.duration && notification.duration > 0 && (
        <div className="absolute bottom-0 left-0 h-1 w-full bg-zinc-200 dark:bg-zinc-700">
          <div
            className={cn('h-full transition-all duration-100 ease-linear', config.progressColor)}
            style={{ width: `${progress}%` }}
          />
        </div>
      )}
    </div>
  );
}
